import { getByPath, pickStringByPaths } from "./path";

export type LineLinkStatus = "unlinked" | "pending" | "linked" | "error";

export type LineLinkSnapshot = {
  status: LineLinkStatus;
  notificationUserId?: string;
  updatedAt?: string;
};

const LINE_LINK_STORAGE_KEY = "wizmate.line_link.v1";

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function getBooleanByPaths(obj: any, paths: string[]): boolean | undefined {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const lowered = value.trim().toLowerCase();
      if (["true", "1", "linked", "active", "connected"].includes(lowered)) return true;
      if (["false", "0", "unlinked", "inactive", "disconnected"].includes(lowered)) return false;
    }
  }

  return undefined;
}

function getStatusByPaths(obj: any, paths: string[]): LineLinkStatus | undefined {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (typeof value !== "string") continue;

    const lowered = value.trim().toLowerCase();
    if (["linked", "active", "connected", "done", "completed"].includes(lowered)) return "linked";
    if (["pending", "waiting", "issued"].includes(lowered)) return "pending";
    if (["error", "failed"].includes(lowered)) return "error";
    if (["unlinked", "none", "inactive"].includes(lowered)) return "unlinked";
  }

  return undefined;
}

export function readLineLinkSnapshot(): LineLinkSnapshot {
  if (typeof window === "undefined") {
    return { status: "unlinked" };
  }

  const raw = window.localStorage.getItem(LINE_LINK_STORAGE_KEY);
  if (!raw) return { status: "unlinked" };

  try {
    const parsed = JSON.parse(raw);
    const status = parsed?.status;
    if (!["unlinked", "pending", "linked", "error"].includes(status)) {
      return { status: "unlinked" };
    }

    return {
      status,
      notificationUserId: normalize(parsed?.notificationUserId),
      updatedAt: normalize(parsed?.updatedAt),
    };
  } catch {
    return { status: "unlinked" };
  }
}

export function writeLineLinkSnapshot(snapshot: LineLinkSnapshot) {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    LINE_LINK_STORAGE_KEY,
    JSON.stringify({
      status: snapshot.status,
      notificationUserId: normalize(snapshot.notificationUserId),
      updatedAt: snapshot.updatedAt ?? new Date().toISOString(),
    })
  );
}

export function markLineLinkPending() {
  const current = readLineLinkSnapshot();
  writeLineLinkSnapshot({
    ...current,
    status: "pending",
    updatedAt: new Date().toISOString(),
  });
}

export function mapLineLinkStatusLabel(status: string | undefined | null): LineLinkStatus {
  const value = typeof status === "string" ? status.trim().toLowerCase() : "";

  if (
    [
      "連携済み",
      "linked",
      "active",
      "connected",
      "done",
      "completed",
      "enabled",
      "ready",
    ].includes(value)
  ) {
    return "linked";
  }

  if (
    [
      "連携中",
      "pending",
      "waiting",
      "issued",
      "processing",
      "followed",
      "follow",
    ].includes(value)
  ) {
    return "pending";
  }

  if (["連携エラー", "error", "failed", "invalid"].includes(value)) {
    return "error";
  }

  return "unlinked";
}

export function resolveLineLinkSnapshotFromApiStatus(
  data: { status?: string | null; isLinked?: boolean; notificationUserId?: string } | string | undefined | null,
  fallback = readLineLinkSnapshot()
): LineLinkSnapshot {
  if (typeof data === "string" || data == null) {
    const mapped = mapLineLinkStatusLabel(data);

    return {
      status: mapped,
      notificationUserId: mapped === "linked" ? fallback.notificationUserId : undefined,
      updatedAt: new Date().toISOString(),
    };
  }

  if (data.notificationUserId) {
    return {
      status: "linked",
      notificationUserId: data.notificationUserId,
      updatedAt: new Date().toISOString(),
    };
  }

  if (data.isLinked === true) {
    return {
      status: "linked",
      notificationUserId: fallback.notificationUserId,
      updatedAt: new Date().toISOString(),
    };
  }

  if (data.isLinked === false) {
    return {
      status: "unlinked",
      updatedAt: new Date().toISOString(),
    };
  }

  const mapped = mapLineLinkStatusLabel(data.status);

  return {
    status: mapped,
    notificationUserId: mapped === "linked" ? fallback.notificationUserId : undefined,
    updatedAt: new Date().toISOString(),
  };
}

export function resolveLineLinkSnapshotFromUser(user: any, fallback = readLineLinkSnapshot()): LineLinkSnapshot {
  const notificationUserId = pickStringByPaths(user, [
    "line.notificationUserId",
    "data.line.notificationUserId",
    "line.userId",
    "data.line.userId",
    "notificationUserId",
    "data.notificationUserId",
    "lineNotificationUserId",
    "data.lineNotificationUserId",
    "lineUserId",
    "data.lineUserId",
  ]);

  const explicitStatus = getStatusByPaths(user, [
    "line.status",
    "data.line.status",
    "lineLinkStatus",
    "data.lineLinkStatus",
  ]);

  const explicitLinked = getBooleanByPaths(user, [
    "line.isLinked",
    "data.line.isLinked",
    "lineLinked",
    "data.lineLinked",
    "isLineLinked",
    "data.isLineLinked",
  ]);

  let status: LineLinkStatus = fallback.status;

  if (explicitStatus) {
    status = explicitStatus;
  } else if (notificationUserId) {
    status = "linked";
  } else if (explicitLinked === true) {
    status = "linked";
  } else if (explicitLinked === false) {
    status = "unlinked";
  }

  return {
    status,
    notificationUserId: notificationUserId ?? fallback.notificationUserId,
    updatedAt: new Date().toISOString(),
  };
}

export function getLineLinkStatusLabel(snapshot: LineLinkSnapshot): string {
  switch (snapshot.status) {
    case "linked":
      return "連携済み";
    case "pending":
      return "連携中";
    case "error":
      return "連携エラー";
    default:
      return "未連携";
  }
}

export function getLineLinkStatusDescription(snapshot: LineLinkSnapshot): string {
  switch (snapshot.status) {
    case "linked":
      return "LINEでニュースを受け取れます。";
    case "pending":
      return "LINE公式アカウントに連携メッセージを送信して、連携を完了してください。";
    case "error":
      return "連携を完了できませんでした。もう一度お試しください。";
    default:
      return "LINEでニュース通知を受け取るには連携が必要です。";
  }
}

export function getLineLinkActionLabel(snapshot: LineLinkSnapshot): string {
  switch (snapshot.status) {
    case "linked":
      return "連携設定";
    case "pending":
      return "連携を続ける";
    case "error":
      return "やり直す";
    default:
      return "連携する";
  }
}
